(() => {

  const BOOST_SELECTOR = '.boost-button';

  // Known multiplier variant IDs
  const BOOSTERS = [
    { id: 50381350699287, mult: 2 },
    { id: 50381368983831, mult: 5 },
    { id: 50381353615639, mult: 10 },
    { id: 50381353779479, mult: 50 }
  ];

  const BOOSTER_IDS = BOOSTERS.map(b => b.id);
  const MULT_BY_ID = new Map(BOOSTERS.map(b => [b.id, b.mult]));

  // Optional: keep your existing 10X cleanup behavior if you still need it
  const TEN_X_TITLE_MATCH = '10X ENTRY BOOSTER';

  // Storage key for “last clicked wins”
  const LAST_PICK_KEY = 'ff_last_booster_variant';

  // Internal guard to prevent loops
  let isNormalizing = false;

  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const getButtons = () => qsa(BOOST_SELECTOR);
  const setActiveUI = (variantId) => {
    getButtons().forEach((btn) => {
      const active = Number(btn.dataset.variantId) === Number(variantId);
      btn.classList.toggle('selected', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };
  const clearActiveUI = () => {
    getButtons().forEach((btn) => {
      btn.classList.remove('selected');
      btn.setAttribute('aria-pressed', 'false');
    });
  };

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error(`cart.js failed: ${res.status}`);
    return res.json();
  }

  async function cartUpdate(updates) {
    // Tag our own request so our fetch-wrapper knows to ignore it
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-FF-Multiplier-Enforce': '1'
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({ updates })
    });
    if (!res.ok) throw new Error(`cart/update.js failed: ${res.status}`);
    return res.json();
  }

  function chooseWinnerVariantId(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const boostersInCart = items.filter(it => BOOSTER_IDS.includes(Number(it.variant_id)));

    if (!boostersInCart.length) return null;

    // If user recently clicked something and it exists in cart, that wins
    const lastPick = Number(sessionStorage.getItem(LAST_PICK_KEY) || 0);
    if (lastPick && boostersInCart.some(it => Number(it.variant_id) === lastPick)) {
      return lastPick;
    }

    // Else highest multiplier wins
    boostersInCart.sort((a, b) => {
      const ma = MULT_BY_ID.get(Number(a.variant_id)) || 0;
      const mb = MULT_BY_ID.get(Number(b.variant_id)) || 0;
      return mb - ma;
    });

    return Number(boostersInCart[0].variant_id);
  }

  function buildExclusiveUpdates(winnerId) {
    const updates = {};
    for (const id of BOOSTER_IDS) {
      updates[id] = winnerId && Number(id) === Number(winnerId) ? 1 : 0;
    }
    return updates;
  }

  async function normalizeTenXLines() {
    try {
      const cart = await fetchCart();
      const items = Array.isArray(cart?.items) ? cart.items : [];

      const titleNeedle = TEN_X_TITLE_MATCH.trim().toLowerCase();
      if (!titleNeedle) return;

      const tenXItems = items.filter((item) => {
        const t = (item.product_title || item.title || '').toString().toLowerCase();
        return t.includes(titleNeedle);
      });

      if (!tenXItems.length) return;

      const discountedTenX = tenXItems.filter((item) => {
        const hasDiscountAlloc =
          Array.isArray(item.line_level_discount_allocations) &&
          item.line_level_discount_allocations.length > 0;
        const isZeroLinePrice = Number(item.line_price) === 0;
        return hasDiscountAlloc || isZeroLinePrice;
      });

      const fullPriceTenX = tenXItems.filter((item) => {
        const hasDiscountAlloc =
          Array.isArray(item.line_level_discount_allocations) &&
          item.line_level_discount_allocations.length > 0;
        const linePrice = Number(item.line_price || 0);
        return !hasDiscountAlloc && linePrice > 0;
      });

      if (!discountedTenX.length || !fullPriceTenX.length) return;

      const updates = {};
      for (const item of fullPriceTenX) {
        if (item.variant_id) updates[item.variant_id] = 0;
      }

      if (Object.keys(updates).length) {
        await cartUpdate(updates);
      }
    } catch (err) {
      console.error('[ff-multipliers][10x-normalize]', err);
    }
  }

  async function enforceSingleBooster({ refreshUI = true } = {}) {
    if (isNormalizing) return;
    isNormalizing = true;

    try {
      const cart = await fetchCart();
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const boostersInCart = items.filter(it => BOOSTER_IDS.includes(Number(it.variant_id)));

      // None in cart
      if (!boostersInCart.length) {
        if (refreshUI) clearActiveUI();
        return;
      }

      const winnerId = chooseWinnerVariantId(cart);

      // Determine if cart is already “clean”: only winner exists and qty=1
      let needsFix = false;
      for (const it of boostersInCart) {
        const vid = Number(it.variant_id);
        const qty = Number(it.quantity || 0);

        if (vid !== Number(winnerId) && qty > 0) needsFix = true;
        if (vid === Number(winnerId) && qty !== 1) needsFix = true;
      }

      if (!needsFix) {
        if (refreshUI) setActiveUI(winnerId);
        return;
      }

      // Fix it: winner=1, everyone else=0
      const updates = buildExclusiveUpdates(winnerId);
      await cartUpdate(updates);

      if (refreshUI) setActiveUI(winnerId);

      // Sync visuals with actual HTML the theme prints (simplest/most reliable)
      // If you want AJAX refresh instead, hook into Dawn cart rendering events.
      location.reload();
    } catch (err) {
      console.error('[ff-multipliers][enforce]', err);
    } finally {
      isNormalizing = false;
    }
  }

  async function onBoostClick(e) {
    const btn = e.target.closest(BOOST_SELECTOR);
    if (!btn) return;

    // Don’t kill other cart behaviors unnecessarily; just stop default button behavior
    e.preventDefault();

    const variantId = Number(btn.dataset.variantId);
    if (!variantId || !BOOSTER_IDS.includes(variantId)) return;

    const isSelected = btn.classList.contains('selected');

    // Toggle OFF: remove all boosters
    if (isSelected) {
      sessionStorage.removeItem(LAST_PICK_KEY);
      clearActiveUI();

      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');

      try {
        await cartUpdate(buildExclusiveUpdates(null));
        location.reload();
      } catch (err) {
        console.error('[ff-multipliers][remove]', err);
        alert('Could not update booster. Please try again.');
      } finally {
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
      }
      return;
    }

    // Set this one as winner
    sessionStorage.setItem(LAST_PICK_KEY, String(variantId));
    setActiveUI(variantId);

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    try {
      await cartUpdate(buildExclusiveUpdates(variantId));
      location.reload();
    } catch (err) {
      console.error('[ff-multipliers][set]', err);
      alert('Could not update booster. Please try again.');
    } finally {
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  }

  function installFetchGuard() {
    const origFetch = window.fetch;
    if (origFetch.__ffMultipliersWrapped) return;

    const CART_ENDPOINT_RE = /\/cart\/(add|change|update|clear)\.js(\?.*)?$/;

    const wrapped = async function (...args) {
      const res = await origFetch.apply(this, args);

      try {
        const input = args[0];
        const url = typeof input === 'string' ? input : (input && input.url) ? input.url : '';
        const isCartEndpoint = CART_ENDPOINT_RE.test(url);

        const opts = args[1] || {};
        const headers = (opts && opts.headers) || {};
        const tagged = (headers && (headers['X-FF-Multiplier-Enforce'] || headers['x-ff-multiplier-enforce'])) ? true : false;

        // If something else updated the cart, immediately enforce
        if (isCartEndpoint && !tagged) {
          // Wait a microtask so other scripts settle
          queueMicrotask(() => enforceSingleBooster({ refreshUI: true }));
        }
      } catch (e) {
        // noop
      }

      return res;
    };

    wrapped.__ffMultipliersWrapped = true;
    window.fetch = wrapped;
  }

  function installSubmitGuard() {
    const form = document.getElementById('cart');
    if (!form) return;

    const handler = async (e) => {
      // Before form submits, enforce 1 booster (if multiple were injected)
      // If it needs fixing, enforceSingleBooster will reload and cancel submission.
      await enforceSingleBooster({ refreshUI: false });
    };

    // Avoid double-binding
    if (form.__ffSubmitBound) return;
    form.__ffSubmitBound = true;

    form.addEventListener('submit', handler);
  }

  function init() {
    // 10x cleanup (optional)
    normalizeTenXLines();

    // Main enforcement on load
    enforceSingleBooster({ refreshUI: true });

    // Clicks
    document.addEventListener('click', onBoostClick);

    // Harden against bypass via other cart scripts
    installFetchGuard();

    // Harden checkout
    installSubmitGuard();

    // BFCache restore
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) enforceSingleBooster({ refreshUI: true });
    });
  }

  // DOM ready (defer already guarantees parsed, but safe for theme oddities)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Optional external controller
  window.FFSetExclusiveMultiplier = async (variantId) => {
    const vid = variantId ? Number(variantId) : null;
    if (vid && !BOOSTER_IDS.includes(vid)) return;

    if (vid) sessionStorage.setItem(LAST_PICK_KEY, String(vid));
    else sessionStorage.removeItem(LAST_PICK_KEY);

    await cartUpdate(buildExclusiveUpdates(vid));
    location.reload();
  };
})();